import { Tooltip } from 'antd';
import cn from 'classnames';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { ReactSVG } from 'react-svg';
import {
    levelPositionsForFiveMissionsLandscape,
    levelPositionsForFiveMissionsMobile,
    levelPositionsForLandscape,
    levelPositionsForSmlMobile,
    levelPositionsMobile
} from '../../utils/constants';
import BonusCard from '../BonusCard';
import './styles.scss';

interface GameMapProps {
    data: any[];
    onClose?: () => void;
    isLandscape?: boolean;
}

const GameMapMobile: FC<GameMapProps> = ({ data, onClose, isLandscape }) => {
    const [selectedMission, setSelectedMission] = useState<Record<string, string> | null>(null);
    const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
    const [tooltipPosition, setTooltipPosition] = useState<Record<string, number>>({});
    const draggableRef = useRef<any>(null);
    const [y, setY] = useState<number>(0);
    const [x, setX] = useState<number>(0);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);

    const bounds = { top: data?.length <= 5 ? -550 : -800, bottom: 10 };
    const boundsForLandscape = { left: -395, right: 0 };
    const isSmallMobile = window.innerWidth < 370;

    const indexOfInProgress = useMemo(
        () => data?.findIndex((mission) => mission?.status === 'inProgress' || mission?.status === 'optInRequired'),
        [data]
    );

    const levelPositionsToUse = useMemo(() => {
        if (data?.length <= 5) {
            return isLandscape ? levelPositionsForFiveMissionsLandscape : levelPositionsForFiveMissionsMobile;
        } else if (isLandscape) {
            return levelPositionsForLandscape;
        } else {
            return isSmallMobile ? levelPositionsForSmlMobile : levelPositionsMobile;
        }
    }, [data?.length, isLandscape, isSmallMobile]);
    
    
    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        const touch = e.touches[0];
        setTouchStart({ x: touch.clientX, y: touch.clientY });
    };

    const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>, mission: Record<string, string>, i: number) => {
        if (!touchStart) return;

        const touch = e.changedTouches[0];
        const deltaX = Math.abs(touch.clientX - touchStart.x);
        const deltaY = Math.abs(touch.clientY - touchStart.y);

        // Only trigger if movement is minimal (considered a tap)
        if (deltaX < 5 && deltaY < 5) {
            handleMarkClick(mission, i);
        }
    };

    const handleMarkClick = (mission: Record<string, string>, i: number) => {
        setSelectedMission(mission);
        setTooltipPosition(levelPositionsToUse[i]);
        setX(-levelPositionsToUse[i].left / 2.8);
        setY(-levelPositionsToUse[i].top / 1.4);
        setTooltipOpen(true);
    };

    const closeTooltip = (event: React.TouchEvent<HTMLDivElement>) => {
        event.stopPropagation();
        setTooltipOpen(false);
    };

    const memoizedBonusCard = useMemo(() => {
        return selectedMission ? (
            <>
                <div
                    className="close_tooltip"
                    onTouchEnd={(e) => {
                        e.stopPropagation();
                        closeTooltip(e);
                    }}
                >
                  <ReactSVG src="/static/images/close.svg" />
                </div>

                <BonusCard data={selectedMission} onClose={onClose} />
            </>
        ) : null;
    }, [selectedMission, onClose]);

    const moveBox = (direction: 'top' | 'bottom' | 'left' | 'right') => {
        setIsDragging(false);

        const isHorizontal = isLandscape && (direction === 'left' || direction === 'right');
        const delta = isHorizontal ? 50 : 125;

        const currentBounds = isLandscape
            ? { min: boundsForLandscape.left, max: boundsForLandscape.right }
            : { min: bounds.top, max: bounds.bottom };

        (isHorizontal ? setX : setY)((prev) =>
            (direction === 'left' || direction === 'top')
                ? Math.max(prev - delta, currentBounds.min)
                : Math.min(prev + delta, currentBounds.max)
        );
    };

    const getStatusIcon = useMemo(
        () => (status: string) => {
            switch (status) {
                case 'announced':
                    return <img className="icon" src="/static/images/locked.png" alt="locked" />;
                case 'completed':
                    return <img className="icon passed_icon" src="/static/images/passed.png" alt="passed" />;
                default:
                    return null;
            }
        },
        []
    );

    useEffect(() => {
        if (indexOfInProgress < 0) return;
        const { top, left } = levelPositionsToUse[indexOfInProgress];
        setX(-left / 2.8);
        setY(-top / 1.4);
    }, [indexOfInProgress]);

    return (
        <>
            <div className="navigations_of_map">
                <button onClick={() => moveBox(isLandscape ? 'right' : 'bottom')}>
                    <ReactSVG src="/static/images/chevron_left.svg" />
                </button>
                <button onClick={() => moveBox(isLandscape ? 'left' : 'top')}>
                    <ReactSVG src="/static/images/chevron_right.svg" />
                </button>
            </div>

            <Draggable
                nodeRef={draggableRef}
                position={isLandscape ? { x, y: 0 } : { x: 0, y }}
                onDrag={(_, data) => {
                    requestAnimationFrame(() => isLandscape ? setX(data.x) : setY(data.y));
                    setIsDragging(true);
                }}
                onStop={(_, data) => {
                    setIsDragging(false);
                    isLandscape ? setX(data.x) : setY(data.y);
                }}
                axis={isLandscape ? 'x' : 'y'}
                bounds={isLandscape ? boundsForLandscape : bounds}
            >
                <div
                    className={cn('map-container', { mobile_containerFor_maxFive_missions: data?.length <= 5 })}
                    style={{
                        transition: isDragging ? 'none' : 'transform 0.3s ease',
                        transform: `translateY(${isLandscape ? x : y}px)`,
                    }}
                    ref={draggableRef}
                >
                    <div className="map">
                        <Tooltip
                            arrow={false}
                            trigger={'click'}
                            open={tooltipOpen}
                            title={memoizedBonusCard}
                            getPopupContainer={(triggerNode: HTMLElement) => triggerNode.parentElement as HTMLElement}
                            overlayStyle={{ top: isLandscape ? 0 : tooltipPosition['top'] / 1.4, left: isLandscape ? tooltipPosition['left'] / 1.4 : 0, right: isLandscape ? 'unset' : 0 }}
                        >
                            {data.map((_item, i) => (
                                <div
                                    key={i}
                                    className="level"
                                    onClick={() => handleMarkClick(data[i], i)} // Works for desktop
                                    onTouchStart={handleTouchStart}
                                    onTouchEnd={(e) => handleTouchEnd(e, data[i], i)}
                                    style={{ top: `${levelPositionsToUse[i].top}px`, left: `${levelPositionsToUse[i].left}px` }}
                                >
                                    <div
                                        className={cn('marker', {
                                            [data[i]?.status === 'announced' ? 'locked' : data[i]?.status]: !!data[i]?.status,
                                        })}
                                    >
                                        {getStatusIcon(data[i]?.status)}
                                        <img src={data[i]?.img_url} width={isLandscape ? 50 : 66} alt={data[i]?.img_url} />
                                    </div>
                                </div>
                            ))}
                        </Tooltip>
                    </div>
                </div>
            </Draggable>
        </>
    );
};

export default GameMapMobile;
